import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Target, Search, ArrowUpDown } from 'lucide-react';

const SortHeader = ({ label, field, sortState, onSort, className = '' }) => (
  <TableHead className={`cursor-pointer hover:bg-muted/50 ${className}`} onClick={() => onSort(field)}>
    <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortState.field === field ? 'text-primary' : 'text-muted-foreground/50'}`} />
    </div>
  </TableHead>
);

export const ProfitabilityTab = ({ projectProfitability, formatCurrency }) => {
  const [profitabilitySearch, setProfitabilitySearch] = useState('');
  const [sort, setSort] = useState({ field: 'name', order: 'asc' });

  const toggleSort = (field) => {
    setSort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));
  };

  const filtered = useMemo(() => {
    let result = projectProfitability;
    if (profitabilitySearch) {
      const term = profitabilitySearch.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.project_code && p.project_code.toLowerCase().includes(term))
      );
    }
    return [...result].sort((a, b) => {
      let aVal = a[sort.field];
      let bVal = b[sort.field];
      if (typeof aVal === 'string') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return sort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = aVal || 0;
      bVal = bVal || 0;
      return sort.order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [projectProfitability, profitabilitySearch, sort]);

  return (
    <Card className="p-6" data-testid="profitability-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Target className="h-5 w-5" /> Project Profitability
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only view derived from Fee Structure, Labor Costs, and Project Reimbursements
          </p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={profitabilitySearch}
            onChange={(e) => setProfitabilitySearch(e.target.value)}
            placeholder="Filter by code or name..."
            className="pl-9"
            data-testid="profitability-search"
          />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No projects to analyze</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <SortHeader label="Code" field="project_code" sortState={sort} onSort={toggleSort} />
              <SortHeader label="Project" field="name" sortState={sort} onSort={toggleSort} />
              <SortHeader label="Client" field="client_name" sortState={sort} onSort={toggleSort} />
              <SortHeader label="Status" field="status" sortState={sort} onSort={toggleSort} />
              <SortHeader label="Project Value" field="totalProjectValue" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Received" field="paidIncome" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Pending" field="pendingIncome" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Labor Cost" field="laborCost" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Reimbursements" field="reimbursementExpenses" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Billable Hours" field="totalBillableHours" sortState={sort} onSort={toggleSort} className="text-right" />
              <SortHeader label="Profit" field="profit" sortState={sort} onSort={toggleSort} className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.project_id}>
                <TableCell className="text-sm text-primary/70">{p.project_code || '-'}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.client_name || '-'}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    p.status === 'completed' ? 'bg-green-500/20 text-green-600' :
                    p.status === 'active' ? 'bg-blue-500/20 text-blue-600' :
                    p.status === 'on_hold' ? 'bg-amber-500/20 text-amber-600' :
                    'bg-gray-500/20 text-gray-600'
                  }`}>{p.status}</span>
                </TableCell>
                <TableCell className="text-right">{formatCurrency(p.totalProjectValue)}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(p.paidIncome)}</TableCell>
                <TableCell className="text-right text-amber-600">{formatCurrency(p.pendingIncome)}</TableCell>
                <TableCell className="text-right text-orange-600">{formatCurrency(p.laborCost)}</TableCell>
                <TableCell className="text-right text-orange-600">{formatCurrency(p.reimbursementExpenses || 0)}</TableCell>
                <TableCell className="text-right">{p.totalBillableHours.toFixed(1)}h</TableCell>
                <TableCell className={`text-right font-semibold ${p.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(p.profit))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};
